import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

import authorization from '@rygen/rygen-ui/authorization';
import { Loadable } from '@rygen/rygen-ui/model';
import { alphabeticComparator, notNull } from '@rygen/rygen-ui/util';

import {
  RateQuote,
  RateShop,
  RateUsage,
  RoutingGuideClassification,
  type ServiceTypeFilters,
  WellKnownRateStrategy,
} from '@/model';
import { serviceTypeFilterComparator } from '@/util';

import { useRoutingGuide } from './useRoutingGuide';

const sortByName = alphabeticComparator({ isCaseSensitive: false });

export interface UseRateShopResponse {
  acceptedRateQuote: ComputedRef<RateQuote | undefined>;
  additionalRateQuotes: ComputedRef<RateQuote[]>;
  benchmarks: ComputedRef<RateQuote[]>;
  filteredRateQuotes: ComputedRef<RateQuote[]>;
  isAnyServiceTypeFilterSet: ComputedRef<boolean>;
  lowestPriceQuote: ComputedRef<RateQuote | undefined>;
  primaryRateShopQuotes: ComputedRef<RateQuote[]>;
  rateErrors: ComputedRef<RateQuote[]>;
  showRateErrors: ComputedRef<boolean>;
  rateQuotes: ComputedRef<RateQuote[]>;
  requiresReRate: ComputedRef<boolean>;
  serviceTypeFilterCounts: ComputedRef<{ [key: string]: number }>;
  uniqueServiceTypeFilters: ComputedRef<string[]>;
}

interface UseRateShopOptions {
  serviceTypeFilters?: MaybeRefOrGetter<ServiceTypeFilters>;
  acceptedRateQuoteId?: MaybeRefOrGetter<number | undefined>;
}

export const useRateShop = (
  rateShop: MaybeRefOrGetter<Loadable<RateShop>>,
  { acceptedRateQuoteId, ...opts }: UseRateShopOptions,
): UseRateShopResponse => {
  const serviceTypeFilters = computed(() => {
    return toValue(opts.serviceTypeFilters) ?? {};
  });

  const { excludedRates } = useRoutingGuide(rateShop, serviceTypeFilters);

  const acceptedRateQuote = computed(() => {
    const rawRateShop = toValue(rateShop);
    const rawAcceptedRateQuoteId = toValue(acceptedRateQuoteId);
    return rawRateShop.data?.rateQuotes?.find(
      (x) => x.id === rawAcceptedRateQuoteId,
    );
  });

  const requiresReRate = computed(() => {
    const rawRateShop = toValue(rateShop);
    return rawRateShop.data?.requiresReRate ?? false;
  });

  const rateQuotes = computed(() => {
    const rawRateShop = toValue(rateShop);
    return rawRateShop.data?.rateQuotes ?? [];
  });

  const benchmarks = computed(() => {
    return rateQuotes.value
      .filter((rq) => rq.rateUsage === RateUsage.BENCHMARK)
      .sort((a, b) => sortByName(a.contractName ?? '', b.contractName ?? ''));
  });

  const excludedRateIds = computed(() => {
    return excludedRates.value.map((r) => r.id).filter(notNull);
  });

  const primaryRateShopQuotes = computed(() => {
    if (acceptedRateQuote.value) {
      return [acceptedRateQuote.value];
    }

    return (
      rateQuotes.value
        ?.filter((rq) => rq.rateUsage === RateUsage.RATE_SHOP)
        ?.filter((r) => r.id && !excludedRateIds.value.includes(r.id)) || []
    );
  });

  const additionalRateQuotes = computed(() => {
    const rawRateShop = toValue(rateShop);
    const rawAcceptedRateQuoteId = toValue(acceptedRateQuoteId);
    return rawAcceptedRateQuoteId && rawRateShop.data
      ? rawRateShop.data.rateQuotes.filter(
          (x) =>
            x.id !== rawAcceptedRateQuoteId &&
            x.rateUsage === RateUsage.RATE_SHOP,
        )
      : [];
  });

  const rateErrors = computed(() => {
    if (!authorization.hasAuthority('rateShop', 'readUnratedResults')) {
      return [];
    }
    const rawRateShop = toValue(rateShop);
    return rawRateShop.data?.rateErrors ?? [];
  });

  const uniqueServiceTypeFilters = computed(() => {
    const serviceTypesArray = primaryRateShopQuotes.value
      .map((rateQuote) => rateQuote.serviceTypeForFiltering)
      .filter((serviceType, index, self) => self.indexOf(serviceType) === index)
      .sort(serviceTypeFilterComparator);

    const withExcluded = excludedRates.value.length
      ? serviceTypesArray.concat(WellKnownRateStrategy.EXCLUDED)
      : serviceTypesArray;

    return rateErrors.value.length
      ? withExcluded.concat(WellKnownRateStrategy.RATE_ERROR)
      : withExcluded;
  });

  const serviceTypeFilterCounts = computed(() => {
    const counts: { [key: string]: number } = {};
    primaryRateShopQuotes.value.forEach((rateQuote) => {
      const type = rateQuote.serviceTypeForFiltering;
      counts[type] ? counts[type]++ : (counts[type] = 1);
    });
    return counts;
  });

  const isAnyServiceTypeFilterSet = computed(() => {
    return !Object.entries(serviceTypeFilters.value)
      .filter(
        ([key, _value]) =>
          key !== WellKnownRateStrategy.EXCLUDED &&
          key !== WellKnownRateStrategy.RATE_ERROR,
      )
      .map(([_key, value]) => value)
      .every((st) => !st);
  });

  const filteredRateQuotes = computed(() => {
    if (!isAnyServiceTypeFilterSet.value) {
      return primaryRateShopQuotes.value;
    }

    return primaryRateShopQuotes.value.filter((r) => {
      return serviceTypeFilters.value[r.serviceTypeForFiltering];
    });
  });

  const showRateErrors = computed(
    () =>
      rateErrors.value.length > 0 &&
      !!serviceTypeFilters.value[WellKnownRateStrategy.RATE_ERROR],
  );

  const lowestPriceQuote = computed(() => {
    return [...rateQuotes.value]
      .filter(
        (quote) =>
          quote.routingGuideClassification !==
          RoutingGuideClassification.EXCLUDED,
      )
      .filter((quote) => quote.rateUsage === RateUsage.RATE_SHOP)
      .sort((a, b) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0))
      .at(0);
  });

  return {
    acceptedRateQuote,
    additionalRateQuotes,
    benchmarks,
    filteredRateQuotes,
    isAnyServiceTypeFilterSet,
    lowestPriceQuote,
    primaryRateShopQuotes,
    rateErrors,
    rateQuotes,
    showRateErrors,
    requiresReRate,
    serviceTypeFilterCounts,
    uniqueServiceTypeFilters,
  };
};
